import { Box, Flex, styled } from "styled-system/jsx";
import { EnumUserQuickLinksType } from "gql/generated";
import { QuickLink, useQuickLinks } from "./useQuickLinks";
import { Icon, Text } from "@ttc3k/trekker";
import { Calendar, City, List, MessageText, Shop } from "iconoir-react";
import {
  SVGProps,
  ForwardRefExoticComponent,
  RefAttributes,
  useMemo,
} from "react";
import { Link } from "react-router";
import { formatDate, isLandscape } from "shared/utils";
import { useAppContext } from "useAppContext";
import { CloudinaryImage } from "shared/components";
import { css } from "styled-system/css";
import { limitFill } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";

type QuickLinksCardProps = {
  quickLink: QuickLink;
};

const StyledLink = styled(Link, {
  base: {
    display: "flex",
    flexDirection: "column",
    height: "140px",
    width: "140px",
    gap: "100",
    bg: "white",
    border: "1px solid",
    borderColor: "border.lightest",
    outline: "1px solid",
    outlineColor: "transparent",
    borderRadius: "150",
    boxShadow: "100",
    flexShrink: 0,
    overflow: "hidden",
    transitionProperty: "border-color, outline-color",
    transitionDuration: "normal",
    transitionTimingFunction: "default",
    cursor: "pointer",
    _hover: {
      borderColor: "border.light",
      outlineColor: "border.light",
    },
  },
});

export const QuickLinksCard = ({ quickLink }: QuickLinksCardProps) => {
  const { type, title, link, entityId, lastVisited } = quickLink;
  const { addQuickLink } = useQuickLinks();
  const { organizations } = useAppContext();

  const iconMap: Record<
    EnumUserQuickLinksType,
    ForwardRefExoticComponent<
      Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
    >
  > = {
    [EnumUserQuickLinksType.Operator]: List,
    [EnumUserQuickLinksType.Organization]: City,
    [EnumUserQuickLinksType.Storefront]: Shop,
    [EnumUserQuickLinksType.Broadcast]: MessageText,
    [EnumUserQuickLinksType.Event]: Calendar,
  };

  const orgLogo = useMemo(
    () =>
      type === EnumUserQuickLinksType.Organization
        ? organizations.find(org => org._id === entityId)?.logo ?? null
        : null,
    [type, entityId, organizations],
  );

  return (
    <Box p={"1px"}>
      {/* This 1px is to account for the outline on hover so it doesn't get cut off by the parent's overflow: hidden property */}
      <StyledLink
        p={type === EnumUserQuickLinksType.Organization ? "300" : "200"}
        alignItems={orgLogo ? "center" : "flex-start"}
        justifyContent={orgLogo ? "center" : "space-between"}
        to={link}
        onClick={() => addQuickLink(title, link, type, entityId)}
      >
        {orgLogo ? (
          <CloudinaryImage
            image={orgLogo}
            imageStyleProps={{
              className: css({
                w: "full",
                aspectRatio: "unset!",
                objectFit: "scale-down",
                objectPosition: "center",
              }),
            }}
            resize={
              isLandscape(orgLogo)
                ? limitFill().height(90).gravity(autoGravity())
                : limitFill().width(90).gravity(autoGravity())
            }
          />
        ) : (
          <>
            <Flex flexDir={"column"} gap={"50"} minW={0}>
              <Icon Element={iconMap[type]} color={"icon.highlight.light"} />
              <Text
                color={"text.highlight.dark"}
                visual={"smallSemiBold"}
                fontWeight={"semiBold"}
                lineClamp={2}
              >
                {title}
              </Text>
            </Flex>

            {lastVisited && (
              <Text
                visual={"smallTag"}
                fontWeight={"semiBold"}
                color={"text.light"}
              >
                {formatDate(lastVisited, "MMM dd")}
              </Text>
            )}
          </>
        )}
      </StyledLink>
    </Box>
  );
};
